import { Controller } from "@hotwired/stimulus"

// Weekly site walk reminder (Phelipe 2026-09-25, Site Walks list #13: "remind
// the team to walk the site each week, and show which weeks are missing").
// The pill in the Site Walks row header (project_site_walks/_weeks) opens a
// small pop-up - on or off, the day, and who gets it ("Send it to", a
// member-multi-select picker, 2026-09-26: nobody picked = the automatic
// group) - and Save sends it without reloading the page: the pill takes the
// new wording and a toast confirms. SiteWalkReminderJob does the reminding.
//
// The row swaps itself for a fresh copy every 20 s while a walk is being made
// ready to play (site_walks_controller#_refreshRow), taking this pop-up with
// it; one that was open comes straight back, as it was, on the new row.
const openDrafts = new Map()
const DRAFT_KEPT_MS = 2000
const PILL_ON = ["border-orange-300", "text-orange-700"]
const PILL_OFF = ["border-gray-300", "text-gray-600"]

export default class extends Controller {
  static targets = ["pill", "label", "onIcon", "offIcon", "modal", "switch", "knob", "weekday", "error", "saveButton",
    "recipients", "picker", "whoAuto", "whoPicked"]
  static values = { url: String, projectId: Number, enabled: Boolean, weekday: Number, recipients: Array, text: Object }

  connect() {
    const draft = openDrafts.get(this.projectIdValue)
    openDrafts.delete(this.projectIdValue)
    if (draft && Date.now() - draft.at < DRAFT_KEPT_MS) this._show(draft.on, draft.weekday, draft.emails)
  }

  disconnect() {
    if (this._isOpen()) openDrafts.set(this.projectIdValue, { on: this.on, weekday: this._weekday(), emails: this._emails, at: Date.now() })
  }

  // Always opens on what's saved; Cancel leaves it that way.
  open() {
    this._show(this.enabledValue, this.weekdayValue, this.recipientsValue)
  }

  // "Send it to" changed (member-multi-select:change).
  recipientsChanged(event) {
    this._emails = event.detail?.emails || []
    this._paintWho()
  }

  close() {
    if (!this._isOpen()) return
    this.modalTarget.classList.add("hidden")
    this.pillTarget.focus()
  }

  stop(event) {
    event.stopPropagation()
    // The click never reaches the page, so the picker can't see it landed
    // outside its list: close the list here. A row that a pick just redrew is
    // already detached - that click was inside (the picker's own rule).
    const target = event.target
    if (this.hasPickerTarget && target?.isConnected && !this.pickerTarget.contains(target)) this._picker()?._close()
  }

  toggle() {
    this._setSwitch(!this.on)
  }

  async save() {
    if (this._saving) return
    this._saving = true
    this.saveButtonTarget.disabled = true
    this._error(null)
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ enabled: this.on, weekday: this._weekday(), recipient_emails: this._emails || [] })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.reminder) throw new Error(data.error || this.textValue.save_failed)
      this._apply(data.reminder)
      this.close()
      this._showToast(data.reminder.enabled ? this.textValue.saved_on : this.textValue.saved_off)
    } catch (error) {
      this._error(error.message || this.textValue.save_failed)
    } finally {
      this._saving = false
      this.saveButtonTarget.disabled = false
    }
  }

  _show(on, weekday, emails) {
    this._error(null)
    this.weekdayTarget.value = String(weekday)
    this._emails = [...(emails || [])]
    this._syncPicker()
    this._paintWho()
    this._setSwitch(on)
    this.modalTarget.classList.remove("hidden")
    this.switchTarget.focus()
  }

  _picker() {
    return this.hasPickerTarget ? this.application.getControllerForElementAndIdentifier(this.pickerTarget, "member-multi-select") : null
  }

  // A row swapped in fresh connects its picker right after this controller.
  _syncPicker() {
    const picker = this._picker()
    if (picker) picker.setSelection(this._emails)
    else if (this.hasPickerTarget) setTimeout(() => this._picker()?.setSelection(this._emails), 0)
  }

  // Nobody picked: the automatic group's line; somebody picked: "only them".
  _paintWho() {
    const picked = (this._emails || []).length > 0
    if (this.hasWhoAutoTarget) this.whoAutoTarget.hidden = picked
    if (this.hasWhoPickedTarget) this.whoPickedTarget.hidden = !picked
  }

  _isOpen() {
    return this.hasModalTarget && !this.modalTarget.classList.contains("hidden")
  }

  _setSwitch(on) {
    this.on = on
    this.switchTarget.setAttribute("aria-checked", String(on))
    this.switchTarget.classList.toggle("bg-brand", on)
    this.switchTarget.classList.toggle("bg-gray-200", !on)
    this.knobTarget.classList.toggle("translate-x-6", on)
    this.knobTarget.classList.toggle("translate-x-1", !on)
    // The day and who gets it only matter while it's on.
    this.weekdayTarget.disabled = !on
    if (this.hasRecipientsTarget) {
      this.recipientsTarget.classList.toggle("opacity-50", !on)
      this.recipientsTarget.classList.toggle("pointer-events-none", !on)
      this.recipientsTarget.setAttribute("aria-disabled", String(!on))
      if (!on) this._picker()?._close()
    }
  }

  _weekday() {
    return Number(this.weekdayTarget.value)
  }

  // What the server saved is what the pill says.
  _apply(reminder) {
    this.enabledValue = reminder.enabled
    this.weekdayValue = reminder.weekday
    this.recipientsValue = reminder.recipients || []
    this.labelTarget.textContent = reminder.label
    this.onIconTarget.hidden = !reminder.enabled
    this.offIconTarget.hidden = reminder.enabled
    PILL_ON.forEach((name) => this.pillTarget.classList.toggle(name, reminder.enabled))
    PILL_OFF.forEach((name) => this.pillTarget.classList.toggle(name, !reminder.enabled))
  }

  _error(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.classList.toggle("hidden", !message)
  }

  // The same toast as the rest of the row (site_walks_controller#_showToast).
  _showToast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
